<template>
  <div
    :class="{
      'fs-input': true,
      'fs-input--disabled': isDisabled,
      'fs-input--error': isError,
      'fs-input--search': isSearch,

      'fs-input--focus': isInputOnFocus,

      [`fs-input--color-${color}`]: true,

      'fs-input--mid-code': variant === 'mid-code',
      'fs-input--mid': variant === 'mid',
      'fs-input--min-code': variant === 'min-code',

      'fs-input--position-left': position === 'left',
      'fs-input--position-right': position === 'right',
    }"
    @mouseenter="isComponentHovered = true"
    @mouseleave="isComponentHovered = false"
  >
    <label class="fs-input__text-input-label">
      <span v-if="isSearch && !noMagnifierButton" class="fs-input__search-icon" v-html="searchIcon"> </span>

      <span v-if="prefix || slots.prefix" class="fs-input__prefix">
        <slot name="prefix">
          {{ prefix }}
        </slot>
      </span>

      <input
        :id="id"
        ref="input"
        :value="modelValue"
        :class="{
          'input-itself': true,
          'input-itself--disabled': isDisabled,
          'input-itself--text-align-center': textAlignCenter,
          'input-itself--additional-right-padding': isDisabled,
          'input-itself--with-suffix': !isInputOnFocus && (suffix || slots.suffix),
          'input-itself--mid': variant === 'mid',
          'input-itself--mid-code': variant === 'mid-code',
          'input-itself--min-code': variant === 'min-code',
          'fs-input__input-itself': true,
          'fs-input__input-itself--smaller-width': isSearch,
        }"
        :type="inputType"
        :readonly="isReadonly"
        :disabled="isDisabled"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :tabindex="tabindex !== undefined ? tabindex : 0"
        :min="min || Number(min) === 0 ? min : ''"
        :step="step"
        @keyup.enter="emits('keyup-enter', $event)"
        @focus="
          isInputOnFocus = true;
          emits('focus');
        "
        @blur="
          isInputOnFocus = false;
          emits('blur');
        "
        @change="realChange"
        @input="onInput"
      />

      <span v-if="!isClearButtonVisible && (suffix || slots.suffix)" class="fs-input__suffix">
        <slot name="suffix">
          {{ suffix }}
        </slot>
      </span>
    </label>

    <span
      v-if="isExpandButtonVisible"
      :class="{
        'fs-input__arrow-button': true,
        'fs-input__arrow-button--flip': !isExpanded,
      }"
      @click="toggleExpand"
      v-html="arrowUpIcon"
    />

    <span v-if="isClearButtonVisible" class="fs-input__cross-button" @click="resetInput" v-html="crossIcon"> </span>

    <fs-tooltip
      v-if="isDisabled && disabledTooltip"
      :text="disabledTooltip"
      class="fs-input__lock-icon-tooltip-wrapper"
    >
      <span
        :class="{
          'fs-input__lock-icon': true,
          'fs-input__lock-icon--with-tooltip': true,
        }"
        v-html="lockIcon"
      ></span>
    </fs-tooltip>

    <span v-else-if="isDisabled" class="fs-input__lock-icon" v-html="lockIcon"></span>
  </div>
</template>

<script setup lang="ts">
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import { computed, nextTick, onMounted, ref, useSlots, useTemplateRef, watch } from 'vue';
import lockIcon from '@icons/other/ico-lock.svg';
import crossIcon from '@icons/other/ico-cross.svg';
import searchIcon from '@icons/other/ico-search.svg';
import arrowUpIcon from '@icons/other/arrow-up.svg';

const emits = defineEmits<{
  focus: [];
  blur: [];
  'keyup-enter': [event: KeyboardEvent];
  'reset-input': [];
  change: [newValue: string | number | null, oldValue: string | number | null];
  'real-change': [newValue: string | number | null, oldValue: string | number | null];
}>();

const isExpanded = defineModel<boolean>('isExpanded', {
  default: false,
});

const [modelValue, modifiers] = defineModel<string | number | null>('modelValue', {
  default: null,
});

const shouldParseAsNumber = computed(() => {
  return props.type === 'number' || modifiers.number;
});

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    variant?: 'standard' | 'mid-code' | 'mid' | 'min-code';
    type?: 'text' | 'email' | 'number' | 'password';
    id?: string;
    color?: 'default' | 'white';

    isExpandable?: boolean;
    isSearch?: boolean;

    isReadonly?: boolean;
    isDisabled?: boolean;
    disabledTooltip?: string; // Tooltip to show when hovering lock icon
    isError?: boolean;
    autocomplete?: string;
    tabindex?: number;
    textAlignCenter?: boolean;
    suffix?: string;
    prefix?: string;

    // attributes used for input number
    min?: string | number;
    max?: string | number;
    step?: string | number;

    position?: 'left' | 'right';

    // Use with care, these one changes the intended behavior of the component
    noClearButton?: boolean;
    forceShowClearButton?: boolean;
    noMagnifierButton?: boolean;
    autoFocus?: boolean;
  }>(),
  {
    variant: 'standard',
    type: 'text',
    color: 'default',
    autocomplete: 'none',
    step: 'any',
    autoFocus: false,
  }
);

const slots = useSlots();
defineSlots<{
  prefix?: () => any;
  suffix?: () => any;
}>();

const previousValue = ref(modelValue.value);

const isComponentHovered = ref(false);
const isInputOnFocus = ref(false);

const CARET_TYPES = new Set(['text', 'search', 'tel', 'url', 'password']);

const inputRef = useTemplateRef('input');
onMounted(() => {
  if (props.autoFocus) {
    nextTick(() => {
      focus();
    });
  }
});

const isClearButtonVisible = computed(() => {
  return props.forceShowClearButton || (hasText.value && props.isSearch && !props.noClearButton);
});

const isExpandButtonVisible = computed(() => {
  return props.isExpandable && !isClearButtonVisible.value;
});

const hasText = computed(() => {
  return modelValue.value && String(modelValue.value).length > 0;
});

watch(
  () => modelValue.value,
  (newValue, oldValue) => {
    emits('change', newValue, oldValue);
  }
);

const inputType = computed(() => {
  return props.type === 'number' ? 'text' : props.type;
});

function realChange(event: Event) {
  let value = (event.target as HTMLInputElement).value;
  const oldValue = previousValue.value;
  let finalValue: string | number | null = value;

  if (shouldParseAsNumber.value) {
    value = value.replace(',', '.');
    const parsed = parseFloat(value);
    finalValue = isNaN(parsed) ? null : parsed;
  }

  modelValue.value = finalValue;
  emits('real-change', finalValue, oldValue);
  previousValue.value = value;
}

function onInput(event: Event) {
  modelValue.value = (event.target as HTMLInputElement).value;
}

function resetInput() {
  // A search field's value is a query string, read with `.toLowerCase()` and friends by whatever
  // filters on it, so clearing it has to leave the string the user would have left by erasing the
  // last character. Elsewhere — a number, a value that is either set or not — null is the empty.
  modelValue.value = props.isSearch ? '' : null;
  emits('reset-input');
  focus();
}

function toggleExpand() {
  isExpanded.value = !isExpanded.value;
  focus();
}

function focus(options?: FocusOptions) {
  inputRef.value?.focus(options);
}
function select() {
  inputRef.value?.select();
}

/**
 * For a caller that has already settled where in the value the caret belongs. Ignored by the
 * types that have no text selection to place it in — a number or an email field answers a
 * caret with an error rather than a caret.
 */
function setCaret(offset: number) {
  const el = inputRef.value;
  if (!el || !CARET_TYPES.has(el.type)) return;
  el.setSelectionRange(offset, offset);
}
/**
 * Where the caret is, or what is selected, for a caller that inserts into the value at that
 * point. Survives the input losing focus, so a menu opened beside the field still reads the
 * place the user left. Null for the types that keep no selection.
 */
function selection(): { start: number; end: number } | null {
  const el = inputRef.value;
  if (!el || !CARET_TYPES.has(el.type) || el.selectionStart === null || el.selectionEnd === null) return null;
  return { start: el.selectionStart, end: el.selectionEnd };
}
defineExpose({
  focus,
  select,
  setCaret,
  selection,
});
</script>

<style scoped lang="scss">
@use 'sass:math';
@import '@styles/popover.scss';
@import '@styles/variables';

.input-itself {
  width: 100%;
  height: 100%;
  padding: 0.1rem 0 0;
  background: none;
  margin: 0;
  border: none;
  color: inherit;
  font-size: 1.4rem;
  line-height: 1.4rem;
  outline: none;

  /* Remove arrows in Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Remove arrows in Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }

  &::placeholder {
    color: rgba($c-dark, 0.5);
  }

  &[value=''] + .fs-input__cross-button {
    display: none;
  }

  &--with-suffix {
    -webkit-mask-image: linear-gradient(90deg, rgba(0, 0, 0, 1) 82%, rgba(0, 0, 0, 0) 100%);
    mask-image: linear-gradient(90deg, rgba(0, 0, 0, 1) 82%, rgba(0, 0, 0, 0) 100%);
  }

  &--disabled {
    cursor: not-allowed;
  }

  &--cursor-pointer {
    cursor: pointer;
  }

  &--text-align-center {
    text-align: center;
  }

  &--additional-right-padding {
    padding-right: 1.5rem;
  }

  &--mid {
    font-size: 1.2rem;
    line-height: 1.2rem;
  }
}

.fs-input {
  height: 3.7rem;
  border-radius: 1.9rem;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
  color: rgba($c-dark, 1);
  transition: 0.1s;
  user-select: none;

  &--color-default {
    background: rgba($c-bg-gray-darkest, 0.5);

    &:hover {
      background: rgba($c-bg-gray-darkest, 0.75);
    }

    &.fs-input--focus,
    &.fs-input--focus:hover {
      background: rgba($c-bg-gray-darkest, 1);
    }
  }

  &--color-white {
    background: rgba($c-bg-light, 0.8);

    &:hover {
      background: rgba($c-bg-light, 0.9);
    }

    &.fs-input--focus,
    &.fs-input--focus:hover {
      background: rgba($c-bg-light, 1);
    }
  }

  &--position-left {
    flex: 1;
    border-radius: 1.9rem 0 0 1.9rem;
    border-right: 1px solid rgba($c-dark, 0.1);
  }

  &--position-right {
    flex: 1;
    border-radius: 0 1.9rem 1.9rem 0;
    border-left: 1px solid rgba($c-dark, 0.1);
  }

  &:hover {
    color: rgba($c-dark, 0.75);
  }

  &--focus,
  &--focus:hover {
    color: rgba($c-dark, 1);
  }

  &--error {
    background: #ffbcbc !important;
  }

  &--disabled {
    color: rgba($c-dark, 0.25) !important;
    background: rgba($c-bg-gray-darkest, 0.25) !important;
  }

  &--mid-code {
    height: 3.1rem;
    border-radius: 1.6rem;
  }

  &--mid {
    height: 3.1rem;
    border-radius: 1.6rem;
  }

  &--min-code {
    height: 2.5rem;
    border-radius: 0.5rem;
  }

  // Position + variant combinations
  &--mid#{&}--position-left,
  &--mid-code#{&}--position-left {
    border-radius: 1.6rem 0 0 1.6rem;
  }

  &--mid#{&}--position-right,
  &--mid-code#{&}--position-right {
    border-radius: 0 1.6rem 1.6rem 0;
  }

  &--min-code#{&}--position-left {
    border-radius: 0.5rem 0 0 0.5rem;
  }

  &--min-code#{&}--position-right {
    border-radius: 0 0.5rem 0.5rem 0;
  }

  &__text-input-label {
    width: 100%;
    height: 100%;
    padding: 0 2rem;
    box-sizing: border-box;
    display: flex;
    align-content: center;
    align-items: center;
    font-size: 1.4rem;
    line-height: 1.4rem;
    cursor: text;
  }

  &--mid-code &__text-input-label {
    padding: 0 1.6rem;
  }

  &--mid &__text-input-label {
    padding: 0 1.6rem;
    font-size: 1.2rem;
    line-height: 1.2rem;
  }

  &--min-code &__text-input-label {
    padding: 0 0.8rem 0;
  }

  &__input-itself {
    &--smaller-width {
      width: calc(100% - #{5.5rem});
    }
  }

  &__lock-icon-tooltip-wrapper {
    width: 1rem;
    height: 1.2rem;
    position: absolute;
    z-index: 2;
    inset: calc(50% - #{0.5rem}) 1.2rem auto auto;
  }

  &__lock-icon {
    width: 1.1rem;
    height: 1.4rem;
    position: absolute;
    z-index: 2;
    inset: auto 1.6rem auto auto;
    pointer-events: none;

    &--with-tooltip {
      inset: auto;
      pointer-events: auto;
    }
  }

  &--mid &__lock-icon,
  &--min-code &__lock-icon {
    width: 1rem;
    height: 1.2rem;
  }

  &--min-code &__lock-icon {
    inset: auto 0.8rem auto auto;
  }

  &__search-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    margin-right: 0.8rem;

    :deep(svg) {
      width: 1.4rem;
      height: 1.4rem;
      fill: rgba($c-dark, 0.7);
    }
  }

  &__clip-icon,
  &__external-link-icon {
    width: 1.5rem;
    height: 1.5rem;
    margin-right: 0.5rem;
    margin-left: 0.5rem;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    pointer-events: none;

    :deep(svg) {
      fill: rgba($c-dark, 0.6);
    }
  }
  &__external-link-icon {
    position: relative;
    top: -1px;
  }

  &__prefix,
  &__suffix {
    height: 100%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    position: relative;
    color: rgba($c-dark, 0.5);
    pointer-events: none;
    user-select: none;
  }

  &__prefix {
    padding-right: 1rem;
    justify-content: flex-start;
    font-size: 1.6rem;
    line-height: 1.9rem;
  }

  &__suffix {
    padding-left: 1rem;
    justify-content: flex-end;
    font-size: 1.4rem;
    line-height: 1.4rem;
  }

  &--min-code &__suffix {
    font-size: 1rem;
    line-height: 1rem;
  }

  &--mid &__prefix,
  &--min-code &__prefix {
    font-size: 1.4rem;
    line-height: 1.7rem;
  }

  &__arrow-button,
  &__cross-button {
    width: 1.1rem;
    height: 1.1rem;
    position: absolute;
    inset: 40% 2rem auto auto;
    z-index: 2;
    cursor: pointer;

    &::before {
      content: '';
      width: calc(100% + #{1rem});
      height: calc(100% + #{1rem});
      position: absolute;
      inset: 50% auto auto 50%;
      transform: translate(-50%, -50%);
    }

    :deep(svg) {
      fill: rgba($c-dark, 0.6);
    }
  }

  &__cross-button {
    width: 0.9rem;
    height: 0.9rem;
    inset: 40% 2.3rem auto auto;

    :deep(svg) {
      fill: $c-dark;
    }

    &:hover {
      opacity: 0.75;
    }
  }

  &__arrow-button {
    width: 1rem;
    height: 1rem;
    inset: 40% 2rem auto auto;
    transition: transform 0.2s ease-in-out;

    :deep(svg) {
      fill: rgba($c-dark, 0.5);
    }

    &:hover {
      :deep(svg) {
        fill: $c-dark;
      }
    }

    &--flip {
      transform: rotate(180deg);
    }
  }
}
</style>
